import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import './Register.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Alert from '../../components/Alert/Alert';
import latinCountries from '../../utils/Countries/latin-america-countries.json';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import LogoMinymol from '../../components/LogoMinymol/LogoMinymol';
import Flag from 'react-world-flags';
import CodigoInput from '../../components/CodigoInput/CodigoInput';

const confirmPhoneCode = async (phoneNumber, code) => {
  return axios.post('https://api.minymol.com/notifications/verify/confirm', {
    channel: 'whatsapp',
    type: 'verify_phone',
    phoneNumber,
    code,
  });
};

const resendPhoneVerificationCode = async (phoneNumber) => {
  return axios.post('https://api.minymol.com/notifications/verify/resend', {
    channel: 'whatsapp',
    type: 'verify_phone',
    phoneNumber,
  });
};

const options = latinCountries.map((country) => ({
  label: (
    <div className="country-option">
      <Flag code={country.value} style={{ width: 20, height: 15, borderRadius: 2 }} />
      <span>{country.label}</span>
    </div>
  ),
  value: country.value,
  code: country.dial_code,
  example: country.example,
}));

const checkIfPhoneExists = async (numeroLocal) => {
  try {
    const res = await axios.get(`https://api.minymol.com/users/resolve-phone/${numeroLocal}`);
    return res.data.phoneNumber; // existe => ya registrado
  } catch (err) {
    return null; // no existe => libre para registrar
  }
};

const Register = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [filteredCities, setFilteredCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(
    options.find(opt => opt.value === 'CO')
  );
  const [localCell, setLocalCell] = useState('');

  const [internationalCell, setInternationalCell] = useState('');
  
  // 🎯 NUEVO: Estado para manejar el código de partner
  const [partnerCode, setPartnerCode] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);

  const [alertType, setAlertType] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPass, setShowPass] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false); // 🆕 Estado de loading
  const [formData, setFormData] = useState({
    code: '',
    password: '',
    repeatPassword: '',
    firstName: '',
    lastName: '',
    cell: '',
  });

  useEffect(() => {
    const savedStep = parseInt(localStorage.getItem('registerStep'), 10);
    const savedIntlCell = localStorage.getItem('registerCellIntl');

    if (savedIntlCell) setInternationalCell(savedIntlCell);
    if (!isNaN(savedStep) && savedStep >= 1 && savedStep <= 4) {
      setStep(savedStep);
    }

    // 🎯 NUEVO: Capturar código de partner de la URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref') || urlParams.get('partner'); // 🔧 Acepta ambos parámetros
    
    if (refCode) {
      setPartnerCode(refCode);
      // Verificar que el código existe
      checkPartnerCode(refCode);
    }
  }, []);

  // 🎯 NUEVO: Función para verificar código de partner
  const checkPartnerCode = async (code) => {
    try {
      const response = await axios.get(`https://api.minymol.com/earnings/public/partner-info/${code}`);
      
      if (response.data.found) {
        setPartnerInfo(response.data.partner);
      } else {
        setPartnerCode(null);
        setPartnerInfo(null);
      }
    } catch (error) {
      console.error('Error verificando código de partner:', error);
      setPartnerCode(null);
      setPartnerInfo(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

  };

  const passwordValidation = {
    hasLetter: /[a-zA-Z]/.test(formData.password),
    hasNumberOrSymbol: /[\d\W]/.test(formData.password),
    hasMinLength: formData.password.length >= 10,
  };

  const nextStep = async () => {
    if (step === 1) {
      if (!selectedCountry || !localCell.trim()) {
        setAlertType('error');
        setAlertMessage('Selecciona un país e ingresa un número de celular.');
        setShowAlert(true);
        return;
      }

      const fullNumber = selectedCountry.dial_code + localCell.replace(/^0+/, ''); // quita ceros al inicio

      const parsed = parsePhoneNumberFromString(fullNumber, selectedCountry.value);
      if (!parsed || !parsed.isValid()) {
        setAlertType('error');
        setAlertMessage('Número de celular inválido para el país seleccionado.');
        setShowAlert(true);
        return;
      }

      const e164 = parsed.number;
      setInternationalCell(e164);
      localStorage.setItem('registerCellIntl', e164);
      const local = localCell;

      try {
        const existe = await checkIfPhoneExists(local);

        if (existe) {
          setAlertType('error');
          setAlertMessage('Ese número ya está registrado. Inicia sesión o recupera tu contraseña.');
          setShowAlert(true);
          return;
        }

        await resendPhoneVerificationCode(e164);

        setFormData((prev) => ({ ...prev, cell: local }));
        setAlertType('success');
        setAlertMessage('¡Listo! Te enviamos un código de verificación a tu celular.');
        setShowAlert(true);
        setStep(2);
      } catch (error) {
        setAlertType('error');
        setAlertMessage('No se pudo verificar el número.');
        setShowAlert(true);
      }

      return;
    }


    if (step === 2) {
      if (!formData.code.trim()) {
        setAlertType('error');
        setAlertMessage('Debes ingresar el código de verificación.');
        setShowAlert(true);
        return;
      }

      try {
        await confirmPhoneCode(internationalCell, formData.code.trim());
        setStep(3);
      } catch (error) {
        setAlertType('error');
        setAlertMessage('Código incorrecto o expirado.');
        setShowAlert(true);
      }
      return;
    }

    if (step === 3) {
      if (!formData.password.trim() || !formData.repeatPassword.trim()) {
        setAlertType('error');
        setAlertMessage('Debes completar ambos campos de contraseña.');
        setShowAlert(true);
        return;
      }

      if (formData.password !== formData.repeatPassword) {
        setAlertType('error');
        setAlertMessage('Las contraseñas no coinciden.');
        setShowAlert(true);
        return;
      }

      if (!passwordValidation.hasLetter || !passwordValidation.hasNumberOrSymbol || !passwordValidation.hasMinLength) {
        setAlertType('error');
        setAlertMessage('La contraseña no cumple con los requisitos.');
        setShowAlert(true);
        return;
      }
    }

    const newStep = step + 1;
    setStep(newStep);
    localStorage.setItem('registerStep', newStep.toString());
  };


  const prevStep = () => {
    const newStep = step - 1;
    setStep(newStep);
    localStorage.setItem('registerStep', newStep.toString());
  };

  const sendTokenToBackend = async (token) => {
    try {
      // 🎯 NUEVO: Incluir partnerCode en la petición si existe
      const loginData = { token };
      if (partnerCode) {
        loginData.partnerCode = partnerCode;
      }

      const response = await axios.post(
        'https://api.minymol.com/auth/login',
        loginData
      );

      const userData = response.data;

      localStorage.setItem('usuario', JSON.stringify(userData));
      localStorage.setItem('token', token);

      console.log('Usuario autenticado:', userData);
      
      // 🎯 NUEVO: Mensaje de éxito con referido si aplica
      if (partnerCode && partnerInfo) {
        setAlertType('success');
        setAlertMessage(`¡Registro exitoso! Has sido referido por ${partnerInfo.nombre} 🎉`);
        setShowAlert(true);
      }
      
      navigate('/');
    } catch (error) {
      console.error('Error desde el backend:', error);
      if (error.response?.status === 401) {
        setAlertType('error');
        setAlertMessage('Intentelo de nuevo.');
        setShowAlert(true);
      } else {
        setAlertType('error');
        setAlertMessage('No se pudo autenticar con el servidor');
        setShowAlert(true);
      }
    }
  };

  const completeUserProfile = async (token) => {
    try {
      await axios.post(
        'https://api.minymol.com/users/complete-profile',
        {
          nombre: `${formData.firstName} ${formData.lastName}`,
          telefono: internationalCell,
          departamento: selectedDepartment,
          ciudad: selectedCity,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      console.log('✅ Perfil completado');
    } catch (error) {
      console.error('Error al completar el perfil:', error);
    }
  };

  useEffect(() => {

    if (step === 4 && departments.length === 0) {
      axios.get('https://raw.githubusercontent.com/marcovega/colombia-json/master/colombia.json')
        .then((res) => {
          setDepartments(res.data);
        })
        .catch((err) => {
          console.error('Error al cargar ciudades:', err);
        });
    }
  }, [step, departments.length]);

  const handleDepartmentChange = (selected) => {
    const dept = selected?.value;
    setSelectedDepartment(dept);
    setSelectedCity(null);

    const selectedDept = departments.find((d) => d.departamento === dept);
    if (selectedDept) {
      setFilteredCities(selectedDept.ciudades.map(c => ({ value: c, label: c })));
    } else {
      setFilteredCities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.cell.trim() ||
      !selectedDepartment ||
      !selectedCity
    ) {
      setAlertType('error');
      setAlertMessage('Todos los campos son obligatorios. Por favor completa todos los datos.');
      setShowAlert(true);
      return;
    }

    setIsRegistering(true); // 🆕 Activar loading

    try {
      const fakeEmail = formData.cell.trim() + '@minymol.com';
      const result = await createUserWithEmailAndPassword(auth, fakeEmail, formData.password);

      await updateProfile(result.user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      await result.user.reload();

      const token = await result.user.getIdToken(true);

      await sendTokenToBackend(token);
      await completeUserProfile(token);

      localStorage.removeItem('registerStep');
      localStorage.removeItem('registerEmail');

      setAlertType('success');
      setAlertMessage('Registro existoso.');
      setShowAlert(true);
    } catch (err) {
      console.error('Error al registrar:', err);
      setAlertType('error');
      setAlertMessage('Hubo un error al registrar el usuario');
      setShowAlert(true);
    } finally {
      setIsRegistering(false); // 🆕 Desactivar loading
    }
  };

  const maskPhoneNumber = (phoneNumber = '') => {
    // Solo deja los últimos 3 dígitos visibles
    const visibleDigits = phoneNumber.slice(-3);
    return phoneNumber.slice(0, -3).replace(/\d/g, 'x') + visibleDigits;
  };

  return (
    <>
      {showAlert && (
        <Alert
          type={alertType}
          message={alertMessage}
          onClose={() => setShowAlert(false)}
        />
      )}
      {step !== 1 && <Header minimal={true} />}
      <div className="register-container">

        <form className={`step-wrapper step-${step}`}>
          {step === 1 && (
            <div className="step-one slide-in">
              <LogoMinymol />
              
              <h5>Para hacer tu primer pedido, necesitamos tu número de celular.</h5>
              <p>Crea tu cuenta gratis y empieza a pedirle a mayoristas de todo el país.</p>
              <label htmlFor="cell">Número de celular</label>
              <div className='input-cellphone'>
                <Select
                  options={options}
                  placeholder="Selecciona tu país..."
                  value={selectedCountry}
                  onChange={setSelectedCountry}
                  isSearchable
                  classNamePrefix="country"
                />
                <div className='cellphone-input'>
                  <span className='country-code'>
                    {selectedCountry ? selectedCountry.code : '+57'}
                  </span>
                  <input
                    type="text"
                    name="cell"
                    placeholder={`Ej.: ${selectedCountry?.example || '0912345678'}`}
                    value={localCell}
                    onChange={(e) => setLocalCell(e.target.value)}
                    className='input-register'
                  />
                </div>
              </div>
              <button type="button" onClick={nextStep}>Continuar</button>
              <div className='line'></div>
              <div className='link-login'>
                <p>¿Ya tienes cuenta?</p>
                <button type='button' onClick={() => window.location.href = '/login'}>Inicia sesión</button></div>
            </div>
          )}

          {
            step === 2 && (
              <div className="step register slide-in">
                <div className='step-sup'>
                  <button id="btn-back" type="button" onClick={prevStep}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <div>
                    <h1>Paso 2 de 4</h1>
                    <h2>Confirma tu número de celular</h2>
                  </div>
                </div>

                <label htmlFor="code">
                  Ingresa el código que te enviamos por WhatsApp
                  {internationalCell && (
                    <span className="masked-number"> al número {maskPhoneNumber(internationalCell)}</span>
                  )}
                </label>
                <CodigoInput
                  longitud={6}
                  onComplete={(code) => {
                    setFormData(prev => ({ ...prev, code }));
                  }}
                />
                <button
                  className='send-code'
                  type="button"
                  onClick={async () => {
                    try {
                      await resendPhoneVerificationCode(internationalCell);
                      setAlertType('info');
                      setAlertMessage('Código reenviado con éxito.');
                      setShowAlert(true);
                    } catch (error) {
                      setAlertType('error');
                      setAlertMessage('No se pudo reenviar el código.');
                      setShowAlert(true);
                    }
                  }}
                >
                  Volver a enviar código
                </button>


                <button id="last-btn" type="button" onClick={nextStep}>Siguiente</button>

              </div>
            )
          }

          {step === 3 && (
            <div className="step register slide-in">
              <div className='step-sup'>
                <button id="btn-back" type="button" onClick={prevStep}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div>
                  <h1>Paso 2 de 3</h1>
                  <h2>Crea una contraseña</h2>
                </div>
              </div>

              <label htmlFor="password">Contraseña</label>
              <div className="pass-wrapper">
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder='Ingrese la contraseña'
                />
                <span className="toggle-pass" onClick={() => setShowPass(!showPass)}>
                  <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                </span>
              </div>

              <label htmlFor="repeatPassword">Repetir contraseña</label>
              <div className="pass-wrapper">
                <input
                  type={showRepeat ? 'text' : 'password'}
                  name="repeatPassword"
                  value={formData.repeatPassword}
                  onChange={handleChange}
                  placeholder='Repita la contraseña'
                />
                <span className="toggle-pass" onClick={() => setShowRepeat(!showRepeat)}>
                  <FontAwesomeIcon icon={showRepeat ? faEyeSlash : faEye} />
                </span>
              </div>

              <div className="password-checklist">
                <p className={passwordValidation.hasLetter ? 'valid' : 'invalid'}>
                  {passwordValidation.hasLetter ? ' ✔ ' : ''} Al menos 1 letra
                </p>
                <p className={passwordValidation.hasNumberOrSymbol ? 'valid' : 'invalid'}>
                  {passwordValidation.hasNumberOrSymbol ? ' ✔ ' : ''} Número o carácter especial
                </p>
                <p className={passwordValidation.hasMinLength ? 'valid' : 'invalid'}>
                  {passwordValidation.hasMinLength ? ' ✔ ' : ''} Mínimo 10 caracteres
                </p>
              </div>
              <button id="last-btn" type="button" onClick={nextStep}>Siguiente</button>

            </div>
          )}

          {step === 4 && (
            <div className="step register slide-in">
              <div className='step-sup'>
                <button id="btn-back" type="button" onClick={prevStep}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div>
                  <h1>Paso 3 de 3</h1>
                  <h2>Información personal</h2>
                </div>
              </div>

              <label htmlFor="firstName">Nombre</label>
              <input
                type="text"
                name="firstName"
                placeholder="Ej.: Juan"
                value={formData.firstName}
                onChange={handleChange}
                className='input-register'
              />

              <label htmlFor="lastName">Apellido</label>
              <input
                type="text"
                name="lastName"
                placeholder="Ej.: Pérez"
                value={formData.lastName}
                onChange={handleChange}
                className='input-register last-name'
              />

              <div className='address-cont'>
                <label className='label-register address' htmlFor="department">Departamento</label>
                <Select
                  options={departments.map(dep => ({ value: dep.departamento, label: dep.departamento }))}
                  placeholder="Selecciona tu departamento..."
                  value={selectedDepartment ? { value: selectedDepartment, label: selectedDepartment } : null}
                  onChange={handleDepartmentChange}
                  isClearable
                  classNamePrefix="mi"
                  name='departamento'
                />

                <div className='blank'></div>

                <label className='city address' htmlFor="city">Ciudad</label>
                <Select
                  options={filteredCities}
                  placeholder="Selecciona tu ciudad..."
                  value={selectedCity ? { value: selectedCity, label: selectedCity } : null}
                  onChange={(selected) => setSelectedCity(selected?.value || null)}
                  isClearable
                  classNamePrefix="mi"
                  name='city'
                />
              </div>

              <button 
                id="last-btn" 
                type="submit" 
                onClick={handleSubmit}
                disabled={isRegistering}
                style={{
                  opacity: isRegistering ? 0.7 : 1,
                  cursor: isRegistering ? 'not-allowed' : 'pointer'
                }}
              >
                {isRegistering ? 'Registrando...' : 'Registrarme'}
              </button>
            </div>
          )}
        </form>
      </div>
      <Footer />
    </>
  );
};

export default Register;
